import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUserProfile, getUserItems, getMe, likeUserApi, dislikeUserApi } from '../api/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Loader2, Mail, Star, Repeat, Plus, Share2, ShieldCheck, MapPin, ThumbsUp, ThumbsDown, Flame, Award, Zap } from 'lucide-react';
import ItemCard from '../components/items/ItemCard';
import ImageWithFallback from '../components/common/ImageWithFallback';
import Breadcrumbs from '../components/common/Breadcrumbs';
import SEO from '../components/common/SEO';
import { toast } from 'sonner';

export default function PublicProfile() {
  const { id } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const { data: user, isLoading: isLoadingUser, error: userError } = useQuery({
    queryKey: ['user', 'profile', id],
    queryFn: () => getUserProfile(id),
  });

  const { data: itemsData, isLoading: isLoadingItems } = useQuery({
    queryKey: ['items', 'user', id],
    queryFn: () => getUserItems(id),
  });

  const { data: me } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: getMe,
  });

  const likeMutation = useMutation({
    mutationFn: () => likeUserApi(id),
    onSuccess: (data) => {
      toast.success(data.message || 'Updated trader rating!');
      queryClient.invalidateQueries(['user', 'profile', id]);
    },
    onError: (err) => toast.error(err.message || 'Failed to update rating')
  });

  const dislikeMutation = useMutation({
    mutationFn: () => dislikeUserApi(id),
    onSuccess: (data) => {
      toast.success(data.message || 'Updated trader rating!');
      queryClient.invalidateQueries(['user', 'profile', id]);
    },
    onError: (err) => toast.error(err.message || 'Failed to update rating')
  });

  // Filter items to show only visible ones (backend already does this but double check for available as well)
  const items = (itemsData?.items || []).filter(item => item.is_available !== false);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(t('linkCopied', 'Profile link copied!'));
  };

  if (isLoadingUser) return <div className="flex justify-center mt-20"><Loader2 className="w-12 h-12 animate-spin text-primary" /></div>;
  if (userError) return <div className="text-center mt-20 text-red-500 font-bold">{t('userNotFound', 'User not found')}</div>;

  const likesCount = user.likes?.length || 0;
  const dislikesCount = user.dislikes?.length || 0;
  const fameScore = user.fame_score ?? (likesCount - dislikesCount);
  const hasLiked = me && user.likes?.some(l => (l._id || l) === me._id);
  const hasDisliked = me && user.dislikes?.some(d => (d._id || d) === me._id);
  const totalVotes = likesCount + dislikesCount;
  const positiveRate = totalVotes > 0 ? Math.round((likesCount / totalVotes) * 100) : 100;

  return (
    <div className="container mx-auto p-4 max-w-5xl space-y-8 pb-10">
      <SEO title={user.full_name} />
      <Breadcrumbs items={[
          { label: t('profile'), link: '/profile' },
          { label: user.full_name }
      ]} />
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-primary via-purple-600 to-secondary rounded-[2.5rem] blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
        <div className="relative bg-card rounded-[2.5rem] shadow-2xl p-6 sm:p-8 md:p-12 border border-white/10 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          
          <div className="relative flex flex-col items-center md:flex-row md:items-start md:gap-10">
            <div className="relative shrink-0">
              <div className="w-32 h-32 md:w-40 md:h-40 relative">
                <ImageWithFallback 
                  src={user.avatar || `https://avatar.vercel.sh/${user.email}.svg`} 
                  alt="avatar" 
                  className="relative w-full h-full rounded-full object-cover border-4 border-background shadow-2xl" 
                />
              </div>
              {user.verification_status === 'verified' && (
                <div className="absolute -bottom-2 -right-2 bg-blue-500 text-white p-1.5 rounded-full border-4 border-background shadow-lg" title="Verified Trader">
                  <ShieldCheck size={20} />
                </div>
              )}
            </div>

            <div className="mt-6 md:mt-0 text-center md:text-left flex-grow space-y-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-1">
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-foreground">{user.full_name}</h1>
                    <span className="px-3 py-1 bg-primary/10 text-primary border border-primary/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                      {fameScore >= 20 ? '🏆 Elite Exchanger' : fameScore >= 5 ? '⭐ Trusted Trader' : '🌱 Swapper'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 text-muted-foreground font-bold text-xs uppercase tracking-widest">
                    <div className="flex items-center gap-1.5">
                      <MapPin size={14} className="text-primary" />
                      <span>{user.location || 'Israel'}</span>
                    </div>
                    {user.email && (
                       <div className="flex items-center gap-1.5">
                        <Mail size={14} className="text-primary" />
                        <span>{user.email.split('@')[0]}@***</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <button 
                  onClick={handleShare}
                  className="bg-secondary/50 hover:bg-secondary text-foreground p-3.5 rounded-2xl transition-all active:scale-95 border border-white/5 flex items-center justify-center gap-2"
                  title="Share Trader Profile"
                >
                  <Share2 size={18} />
                </button>
              </div>

              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-2xl italic">
                "{user.bio || t('noBioProvided', 'No bio provided.')}"
              </p>

              {/* Trader Fame & Upvote / Downvote Controls */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!me) return toast.error(t('loginToTrade', 'Please log in to rate traders'));
                    likeMutation.mutate();
                  }}
                  disabled={likeMutation.isLoading || me?._id === user._id}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-black text-xs border transition-all active:scale-95 ${
                    hasLiked 
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50 shadow-md scale-105' 
                      : 'bg-secondary/60 hover:bg-emerald-500/10 text-foreground hover:text-emerald-400 border-white/10'
                  }`}
                  title="Like Trader Reputation"
                >
                  <ThumbsUp size={16} className={hasLiked ? 'fill-current text-emerald-400' : ''} />
                  <span>{likesCount} Like{likesCount !== 1 ? 's' : ''}</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (!me) return toast.error(t('loginToTrade', 'Please log in to rate traders'));
                    dislikeMutation.mutate();
                  }}
                  disabled={dislikeMutation.isLoading || me?._id === user._id}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-black text-xs border transition-all active:scale-95 ${
                    hasDisliked 
                      ? 'bg-rose-500/20 text-rose-400 border-rose-500/50 shadow-md scale-105' 
                      : 'bg-secondary/60 hover:bg-rose-500/10 text-foreground hover:text-rose-400 border-white/10'
                  }`}
                  title="Dislike Trader Reputation"
                >
                  <ThumbsDown size={16} className={hasDisliked ? 'fill-current text-rose-400' : ''} />
                  <span>{dislikesCount} Dislike{dislikesCount !== 1 ? 's' : ''}</span>
                </button>

                <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-500 border border-amber-500/30 rounded-xl text-xs font-black shadow-xs">
                  <Flame size={16} className="fill-current text-amber-400 animate-pulse" />
                  <span>{fameScore > 0 ? `+${fameScore}` : fameScore} Trader Fame</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-white/10 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center group/stat p-3 bg-secondary/30 rounded-2xl border border-white/5">
              <div className="inline-flex p-2.5 bg-purple-500/10 rounded-xl text-purple-500 mb-2">
                <Repeat className="w-5 h-5" />
              </div>
              <h4 className="font-black text-2xl text-foreground block truncate">{user.total_trades || 0}</h4>
              <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mt-1 truncate">{t('totalTrades', 'Total Trades')}</p>
            </div>

            <div className="text-center group/stat p-3 bg-secondary/30 rounded-2xl border border-white/5">
              <div className="inline-flex p-2.5 bg-amber-500/10 rounded-xl text-amber-500 mb-2">
                <Flame className="w-5 h-5 fill-current" />
              </div>
              <h4 className="font-black text-2xl text-foreground block truncate">{fameScore > 0 ? `+${fameScore}` : fameScore}</h4>
              <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mt-1 truncate">Trader Fame</p>
            </div>

            <div className="text-center group/stat p-3 bg-secondary/30 rounded-2xl border border-white/5">
              <div className="inline-flex p-2.5 bg-emerald-500/10 rounded-xl text-emerald-500 mb-2">
                <Award className="w-5 h-5" />
              </div>
              <h4 className="font-black text-2xl text-emerald-400 block truncate">{positiveRate}%</h4>
              <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mt-1 truncate">Positive Feedback</p>
            </div>

            <div className="text-center group/stat p-3 bg-secondary/30 rounded-2xl border border-white/5">
              <div className="inline-flex p-2.5 bg-blue-500/10 rounded-xl text-blue-500 mb-2">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h4 className="font-black text-xl text-foreground block uppercase truncate">{t(user.verification_status || 'unverified')}</h4>
              <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mt-1 truncate">{t('status', 'Status')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        <div className="flex justify-between items-end">
          <div>
            <h2 className="text-4xl font-black bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                {t('userInventory', 'Inventory')}
            </h2>
            <p className="text-muted-foreground font-medium mt-1">{t('browseUserItems', 'Browse items available for trade')}</p>
          </div>
        </div>

        {isLoadingItems ? (
            <div className="flex justify-center py-20"><Loader2 className="w-12 h-12 animate-spin text-primary" /></div>
        ) : items.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {items.map(item => (
                    <ItemCard 
                        key={item._id} 
                        item={item} 
                    />
                ))}
            </div>
        ) : (
            <div className="text-center bg-card rounded-[2.5rem] border border-dashed border-white/10 p-20">
                <h3 className="text-2xl font-black text-foreground mb-2">{t('noPublicItems', "No public items yet")}</h3>
                <p className="text-muted-foreground max-w-sm mx-auto">{t('userHasNoItemsToTrade', "This user doesn't have any visible items to trade right now.")}</p>
            </div>
        )}
      </div>

      {me && me._id !== user._id && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50">
            <Link 
                to={`/browse?user=${user._id}`}
                className="bg-primary text-primary-content px-10 py-5 rounded-[2rem] font-black uppercase tracking-widest shadow-2xl shadow-primary/40 flex items-center gap-3 hover:scale-105 transition-all active:scale-95"
            >
                <Repeat size={24} />
                {t('makeAnOffer', 'Make an Offer')}
            </Link>
        </div>
      )}
    </div>
  );
}
